import { ObjectMap } from '@df/common';
import { z } from 'zod';

const objectMapSchema = <KeySchema extends z.ZodTypeAny, ValueSchema extends z.ZodTypeAny>(
  keySchema: KeySchema,
  valueSchema: ValueSchema,
) =>
  z
    .map(keySchema, valueSchema)
    .transform(<Key, Value>(map: Map<Key, Value>, _ctx: z.RefinementCtx): ObjectMap<Key, Value> => {
      const objectMap: ObjectMap<Key, Value> = new ObjectMap();
      for (const [key, value] of map.entries()) {
        objectMap.set(key, value);
      }
      return objectMap;
    });

// Reject schemas whose *output* can be optional (z.undefined() and z.nullable() are still allowed!)
type NotKeyOptional<TSchema extends z.ZodType> = TSchema['_zod']['optout'] extends 'optional'
  ? never
  : TSchema;

/**
 * Decodes a JSON record into an `ObjectMap`, parsing each string key through
 * the key schema. Use for structural (class) keys — `mapFromRecord` is the
 * plain-`Map` twin for primitive keys.
 */
const objectMapFromRecord = <KeySchema extends z.ZodType, ValueSchema extends z.ZodType>(
  keySchema: KeySchema,
  valueSchema: ValueSchema,
) =>
  z.record(z.string(), valueSchema).transform((record, ctx) => {
    const map = new ObjectMap<z.infer<KeySchema>, z.infer<ValueSchema>>();
    for (const [key, value] of Object.entries(record)) {
      const parsedKey = keySchema.safeParse(key);
      if (parsedKey.success) {
        map.set(parsedKey.data, value);
      } else {
        ctx.addIssue({ code: 'custom', message: parsedKey.error.message });
      }
    }
    return map;
  });

const objectMapFromVecSchema = <KeySchema extends z.ZodType, ValueSchema extends z.ZodType>(
  keySchema: NotKeyOptional<KeySchema>,
  valueSchema: NotKeyOptional<ValueSchema>,
) => {
  const entrySchema = z.tuple([keySchema, valueSchema]);
  return z
    .object({
      map: entrySchema.array(),
    })
    .transform(
      ({ map }) =>
        new ObjectMap(
          // SAFETY: The entrySchema has already parsed the data.
          // For some reason, we lose type information in this transform, making this 'as' necessary.
          map as [z.output<NotKeyOptional<KeySchema>>, z.output<NotKeyOptional<ValueSchema>>][],
        ),
    );
};

export { objectMapFromRecord, objectMapFromVecSchema, objectMapSchema, type NotKeyOptional };
